import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Avatar,
  Checkbox,
  Box,
  Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArchiveIcon from "@mui/icons-material/Archive";
import PersonIcon from "@mui/icons-material/Person";
import { formatSmartDate } from "./utils/dateUtils";

const MessageCard = ({
  message = {},
  onReply,
  onArchive,
  onInbox,
  isSelected,
  onSelect,
  tabIndex,
}) => {
  const [imgError, setImgError] = useState(false);

  const userRole = localStorage.getItem("userRole") || "";
  const isCompany = userRole === "COMPANY" || userRole === "DIRECTOR";

  //  ROLE-BASED IMAGE LOGIC
  const avatarUrl =
    userRole === "USER"
      ? message.directorImageURL ||
        message.directorImage ||
        message.companyLogo
      : message.talentImageURL ||
        message.talentImage;

  //  ROLE-BASED NAME
const senderName =
  message.directorName ||
  message.companyName ||
  message.talentName ||
  "User";

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  if (!message) return null;

  const goToProfile = () => {
    if (isCompany && message.talentId) {
      window.open(`/talent/${message.talentId}`, "_blank", "noopener,noreferrer");
    }
  };

  // remove HTML tags from preview
  const previewText = message.messageText
    ? message.messageText.replace(/<[^>]*>/g, "").trim()
    : "";

  return (
    <Card
      sx={{
        mb: 2,
        px: 2,
        py: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
          },
          gap: 2,
        })}
      >
        {/* LEFT SECTION */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
<Checkbox
  checked={isSelected}
  onChange={onSelect}
  onClick={(e) => e.stopPropagation()} // ← add this
/>

          <Avatar
            variant="rounded"
            src={!imgError && avatarUrl ? avatarUrl : undefined}
            onError={() => setImgError(true)}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              mt: 0.5,
              cursor: isCompany ? "pointer" : "default",
              pointerEvents: isCompany ? "auto" : "none",
              "& img": {
                objectFit: "cover",
                objectPosition: "top",
              },
            }}
            onClick={goToProfile}
          >
            {imgError ? senderName?.charAt(0) : <PersonIcon />}
          </Avatar>

          <Box
            onClick={goToProfile}
            sx={{
              cursor: isCompany ? "pointer" : "default",
              display: "flex",
              minWidth: "200px",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
              pointerEvents: isCompany ? "auto" : "none",
            }}
          >
            <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
              {senderName}
            </Typography>

            {message.status === "r" ? (
              <VisibilityIcon color="success" fontSize="small" />
            ) : (
              <VisibilityOffIcon color="error" fontSize="small" />
            )}
          </Box>
        </Box>

        {/* CENTER SECTION */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 500,
              cursor: "pointer",
              mb: 0.5,
              wordBreak: "break-word",
            }}
            onClick={() =>
  onReply({
    ...message,

    // normalize image before sending
    talentImage:
      message.talentImage ||
      message.talentImageURL ||
      null,

    directorImage:
      message.directorImage ||
      message.directorImageURL ||
      message.companyLogo ||
      null,
  })
}
          >
            {message.noticeTitle
              ? `${message.messageSubject}`
              : message.messageSubject}
          </Typography>

          <Typography
  sx={{
    fontSize: 12,
    color: "gray",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    mb: 0.25,
  }}
>
  {previewText
    ? previewText.length > 100
      ? previewText.substring(0, 100) + "..."
      : previewText
    : "New Attachment Sent"}
</Typography>

          <Typography sx={{ fontSize: 12, color: "gray" }}>
            {formatSmartDate(message.conversationDate)}
          </Typography>
        </Box>

        {/* RIGHT ACTIONS */}
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: 1,
            flexShrink: 0,
            mt: 1,
            [theme.breakpoints.up("md")]: {
              mt: 0,
              alignSelf: "flex-start",
            },
          })}
        >
          {onInbox ? (
        <Button
  variant="outlined"
  startIcon={<ArchiveIcon />}
  size="small"
  onClick={() => onInbox([message.originalMessageId])}
  sx={{
    color: "#3F51B5",
    borderColor: "#3F51B5",
    textTransform: "uppercase",
  }}
>
  {message.origin || "Inbox"}
</Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              size="small"
              onClick={() => onArchive([message.originalMessageId])}
              sx={{
                color: "#90b5fe",
                borderColor: "#90b5fe",
                textTransform: "uppercase",
              }}
            >
              Archive
            </Button>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() =>
  onReply({
    ...message,
    talentImage:
      message.talentImage ||
      message.talentImageURL ||
      null,
    directorImage:
      message.directorImage ||
      message.directorImageURL ||
      message.companyLogo ||
      null,
  })
}
            sx={{
              color: "#90b5fe",
              borderColor: "#90b5fe",
              textTransform: "uppercase",
            }}
          >
            Reply
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default MessageCard;